import { useTranslation } from 'react-i18next';
import { RouteTypeIcon } from '../icons/route-type-icon';
import { cn } from '../../lib/utils';
import type { Climb } from '../../types/climb';
import { knownRouteTypes } from '../../types/route-type';

interface CragRouteDetailsProps {
  climb: Climb;
  /**
   * The route's own page, where this is read rather than glanced at, and where the
   * length and the bolts have no row beside the grade to sit in.
   */
  onPage?: boolean;
  className?: string;
}

/**
 * What the topo says about a route besides its grade and its length: the setter's
 * words and what kind of climbing it is. Nothing at all where nobody has said.
 */
export function CragRouteDetails({ climb, onPage = false, className }: CragRouteDetailsProps) {
  const { t } = useTranslation();
  const description = climb.description?.trim();
  const route = climb.type === 'Route' ? climb : null;
  const routeTypes = knownRouteTypes(route?.route_types);
  // Tested for a number: a cached payload from before a field carries nothing at all.
  const length = onPage && typeof route?.length === 'number' ? route.length : null;
  const bolts = onPage && typeof route?.bolts === 'number' ? route.bolts : null;

  if (!description && routeTypes.length === 0 && length === null && bolts === null) return null;

  return (
    <div data-testid="crag-climb-details" className={cn('space-y-1.5', className)}>
      {(length !== null || bolts !== null) && (
        <p className="flex gap-2 text-sm font-medium">
          {length !== null && <span>{t('cragProfile.lengthMeters', { value: length })}</span>}
          {bolts !== null && <span>{t('cragProfile.boltsCount', { count: bolts })}</span>}
        </p>
      )}
      {description && (
        <p className={cn('break-words text-muted-foreground', onPage ? 'text-sm' : 'text-xs')}>{description}</p>
      )}
      {routeTypes.length > 0 && (
        <ul aria-label={t('cragProfile.routeTypes')} className="flex flex-wrap gap-x-2.5 gap-y-1">
          {routeTypes.map((type) => (
            <li
              key={type}
              className={cn('inline-flex items-center gap-1 text-muted-foreground', onPage ? 'text-xs' : 'text-[11px]')}
            >
              <RouteTypeIcon type={type} className="h-4 w-4 shrink-0" />
              {t(`routeTypes.${type}`)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
